import React, { useMemo, useState } from "react";
import { Checkbox, Segment, Table, Input, Message, Statistic, Icon, Label } from "semantic-ui-react";
import { ESTIMADORES_SIN_INSTRUMENTOS, NO_ESTIMABLES } from "../catalogs/estimadoresSinInstrumentos";
import { estiloDeClave } from "../catalogs/semaforoColores";
import {
  aforoPorLlenado,
  aforoPorCaudal,
  estimacionPorViajes,
  estimacionDomesticaAnual,
  estimacionPorProduccion,
  semaforo,
} from "../waterBalanceCalc";
import { useWaterBalanceT } from "../useWaterBalanceT";

// Ejecuta la fórmula del motor que corresponde a cada estimador. Es glue de
// UI: la aritmética sigue viviendo únicamente en waterBalanceCalc.js.
function ejecutarEstimador(estimador, valores) {
  if (estimador.motor === "directo") return Number(valores.m3) || 0;
  if (estimador.motor === "aforoPorLlenado") {
    return aforoPorLlenado({
      volumenTanqueM3: Number(valores.volumenTanqueM3) || 0,
      minutosEnLlenar: Number(valores.minutosEnLlenar) || 0,
      horasPorDia: Number(valores.horasPorDia) || 0,
      diasPorAnio: Number(valores.diasPorAnio) || 0,
    }).volumenAnualM3;
  }
  if (estimador.motor === "aforoPorCaudal") {
    return aforoPorCaudal({
      litros: Number(valores.litros) || 0,
      segundos: Number(valores.segundos) || 0,
      horasPorDia: Number(valores.horasPorDia) || 0,
      diasPorAnio: Number(valores.diasPorAnio) || 0,
    }).volumenAnualM3;
  }
  if (estimador.motor === "estimacionPorViajes") return estimacionPorViajes({ viajes: valores.viajes, m3PorViaje: valores.m3PorViaje });
  if (estimador.motor === "estimacionDomesticaAnual") return estimacionDomesticaAnual({ personas: valores.personas, dias: valores.dias, litrosPorPersonaDia: valores.litrosPorPersonaDia });
  if (estimador.motor === "estimacionPorProduccion") return estimacionPorProduccion({ kg: valores.kg, relacionDeBanoLKg: valores.relacionDeBanoLKg });
  return 0;
}

// Modo "No tengo medidores": arranca con balde, facturas y papeles. Ningún
// estimador pasa de grado 3 — es un punto de partida, no un balance medido
// (por eso acá NO se pide clasificar el residuo: con esta calidad de dato,
// forzar una clasificación M36 sería aparentar una precisión que no existe).
// `step` es controlado desde afuera (BalanceSidebar + navPorModo.js).
export function ModoDia0Workspace({ step }) {
  const t = useWaterBalanceT();
  const [activos, setActivos] = useState({});
  const [valores, setValores] = useState({});

  const toggle = (id) => setActivos({ ...activos, [id]: !activos[id] });
  const setValor = (id, campo, val) => setValores({ ...valores, [id]: { ...(valores[id] || {}), [campo]: val } });

  const flujos = useMemo(() => {
    return Object.values(ESTIMADORES_SIN_INSTRUMENTOS)
      .filter((e) => activos[e.id])
      .map((e) => ({
        id: e.id,
        nombre: e.nombre,
        lado: e.lado,
        volumenM3: ejecutarEstimador(e, valores[e.id] || {}),
        grado: e.grado,
      }));
  }, [activos, valores]);

  const totalIn = flujos.filter((f) => f.lado === "entrada").reduce((a, f) => a + f.volumenM3, 0);
  const totalOut = flujos.filter((f) => f.lado === "salida").reduce((a, f) => a + f.volumenM3, 0);
  const descuadre = totalIn - totalOut;
  const descuadrePct = totalIn ? (descuadre / totalIn) * 100 : 0;
  const vacio = flujos.length === 0;

  // El semáforo estándar diría "hay que explicar la diferencia" — acá no
  // aplica: en un balance sin instrumentos la mayor parte del descuadre es
  // agua que sale por lugares que todavía no se pueden medir. No es un
  // fracaso, es el punto de partida (mismo criterio que armar() con
  // E.dia0 en el prototipo original).
  const semaforoBase = semaforo({ descuadre, descuadrePct, pendientes: 0, cerrado: true, vacio });
  const semaforoDia0 = vacio
    ? semaforoBase
    : { clave: "d0", severidad: "info", titulo: t("water_balance_dia0_semaforo_titulo"), detalle: t("water_balance_dia0_semaforo_detalle", { pct: descuadrePct.toFixed(1) }) };

  // El primer medidor a instalar: el flujo más grande entre los que
  // quedaron sin cuantificar en absoluto o con el estimador más pobre
  // (grado 2), porque ahí es donde más sube la calidad del dato.
  const recomendacion = [...flujos].sort((a, b) => b.volumenM3 * (5 - b.grado) - a.volumenM3 * (5 - a.grado))[0];

  return (
    <div className="water-balance-workspace">
      <div className="water-balance-workspace__topbar">
        <span className="water-balance-workspace__tag">{t("water_balance_modo_dia0_titulo")}</span>
      </div>

      <div className="water-balance-workspace__main">
          {step === "quetenes" && (
            <div className="water-balance-step">
              <h3>{t("water_balance_dia0_quetenes_title")}</h3>
              <Message>{t("water_balance_dia0_quetenes_hint")}</Message>

              {Object.values(ESTIMADORES_SIN_INSTRUMENTOS).map((e) => (
                <Segment key={e.id}>
                  <Checkbox
                    toggle
                    checked={!!activos[e.id]}
                    onChange={() => toggle(e.id)}
                    label={<label><strong>{e.nombre}</strong> <Label size="tiny" basic>{t("water_balance_grade_label", { grado: e.grado })}</Label></label>}
                  />
                  <p style={{ color: "#666", fontSize: 13, marginTop: 6 }}>{e.ayuda}</p>
                  {activos[e.id] && (
                    <Table basic="very" compact style={{ marginTop: 10 }}>
                      <Table.Body>
                        {e.campos.map((c) => (
                          <Table.Row key={c.key}>
                            <Table.Cell width={6}>{c.label}</Table.Cell>
                            <Table.Cell>
                              <Input
                                size="small"
                                type="number"
                                value={valores[e.id]?.[c.key] || ""}
                                onChange={(ev) => setValor(e.id, c.key, ev.target.value)}
                              />
                            </Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table>
                  )}
                </Segment>
              ))}

              <Message warning size="small">
                <strong>{t("water_balance_dia0_no_estimables_title")}</strong>
                <ul>
                  {NO_ESTIMABLES.map((n) => (
                    <li key={n.id}>{n.nombre}</li>
                  ))}
                </ul>
              </Message>
            </div>
          )}

          {step === "balance" && (
            <div className="water-balance-step">
              <h3>{t("water_balance_dia0_step_balance")}</h3>
              <Statistic.Group widths={3}>
                <Statistic><Statistic.Value>{totalIn.toLocaleString("es-AR")}</Statistic.Value><Statistic.Label>{t("water_balance_stat_entries")}</Statistic.Label></Statistic>
                <Statistic><Statistic.Value>{totalOut.toLocaleString("es-AR")}</Statistic.Value><Statistic.Label>{t("water_balance_stat_outputs")}</Statistic.Label></Statistic>
                <Statistic><Statistic.Value>{descuadre.toLocaleString("es-AR")}</Statistic.Value><Statistic.Label>{t("water_balance_stat_imbalance")} · {descuadrePct.toFixed(1)}%</Statistic.Label></Statistic>
              </Statistic.Group>
              <Message icon style={{ marginTop: 16, ...estiloDeClave(semaforoDia0.clave) }}>
                <Icon name="info circle" />
                <Message.Content>
                  <Message.Header>{semaforoDia0.titulo}</Message.Header>
                  {semaforoDia0.detalle}
                </Message.Content>
              </Message>
            </div>
          )}

          {step === "medidor" && (
            <div className="water-balance-step">
              <h3>{t("water_balance_dia0_step_medidor")}</h3>
              {!recomendacion ? (
                <Message>{t("water_balance_dia0_no_flows_yet")}</Message>
              ) : (
                <Message positive icon="wrench">
                  <Message.Header>{recomendacion.nombre}</Message.Header>
                  {t("water_balance_dia0_medidor_recomendacion", { volumen: recomendacion.volumenM3.toLocaleString("es-AR"), grado: recomendacion.grado })}
                </Message>
              )}
            </div>
          )}

          {step === "informe" && (
            <div className="water-balance-step">
              <h3>{t("water_balance_dia0_step_informe")}</h3>
              <Message warning>{t("water_balance_dia0_informe_disclaimer")}</Message>
              <Table celled compact>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>{t("water_balance_col_name")}</Table.HeaderCell>
                    <Table.HeaderCell>{t("water_balance_datos_col_side")}</Table.HeaderCell>
                    <Table.HeaderCell textAlign="right">m³</Table.HeaderCell>
                    <Table.HeaderCell textAlign="right">{t("water_balance_col_grade")}</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {flujos.map((f) => (
                    <Table.Row key={f.id}>
                      <Table.Cell>{f.nombre}</Table.Cell>
                      <Table.Cell>{f.lado === "entrada" ? t("water_balance_datos_side_in") : t("water_balance_datos_side_out")}</Table.Cell>
                      <Table.Cell textAlign="right">{f.volumenM3.toLocaleString("es-AR")}</Table.Cell>
                      <Table.Cell textAlign="right">{f.grado}</Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </div>
          )}
      </div>
    </div>
  );
}

export default ModoDia0Workspace;
